import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import {
  Article,
  CheckCircle,
  Cancel,
  OpenInNew,
} from "@mui/icons-material";
import { reviewProviderDocument } from "../../utils/documentUploadAdminService";

/**
 * Formats bytes for compact display.
 *
 * @param {number} bytes
 * @returns {string}
 */
const formatBytes = (bytes) => {
  const value = Number(bytes || 0);
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

const DOCUMENT_TYPE_LABELS = {
  passport: "Passport",
  driving_licence: "Driving Licence",
  driving_license: "Driving Licence",
  drivingLicence: "Driving Licence",
  drivingLicense: "Driving Licence",
  resume_cv: "Resume/CV",
  resume: "Resume/CV",
  cv: "Resume/CV",
  certificates: "Certificates",
  certificate: "Certificates",
  verification_video: "Verification Video",
  verificationVideo: "Verification Video",
};

const SIDE_LABELS = {
  front: "Front",
  back: "Back",
  single: "",
  video: "",
};

const formatDocumentLabel = (documentType, side) => {
  const baseLabel = DOCUMENT_TYPE_LABELS[documentType] || documentType || "Document";
  const sideLabel = SIDE_LABELS[side] ?? side;
  return sideLabel ? `${baseLabel} - ${sideLabel}` : baseLabel;
};

/**
 * Shows one secure provider document for admin review.
 *
 * @param {Object} props
 * @param {Object} props.document
 * @param {Function} [props.onReviewed]
 * @returns {JSX.Element}
 */
const DocumentReviewCard = ({ document, onReviewed }) => {
  const [rejectionReason, setRejectionReason] = useState("");
  const [submitting, setSubmitting] = useState("");
  const [error, setError] = useState("");

  const review = async (status) => {
    setSubmitting(status);
    setError("");

    try {
      const result = await reviewProviderDocument({
        documentId: document.id,
        status,
        rejectionReason,
      });
      onReviewed?.(result.document);
    } catch (reviewError) {
      setError(reviewError.message || "Document review failed.");
    } finally {
      setSubmitting("");
    }
  };

  const normalizedStatus = String(document.status || "").toLowerCase();
  const normalizedReviewStatus = String(document.reviewStatus || "").toLowerCase();
  const isApproved = normalizedReviewStatus === "approved" || normalizedStatus === "approved";
  const isRejected =
    ["rejected", "reupload_required"].includes(normalizedReviewStatus) ||
    ["rejected", "reupload_required"].includes(normalizedStatus);
  const statusLabel =
    normalizedStatus === "reupload_required" ||
    normalizedReviewStatus === "reupload_required"
      ? "re-upload required"
      : document.reviewStatus || document.status || "pending";

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, width: "100%", minWidth: 0 }}>
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", minWidth: 0 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            bgcolor: "#eef2ff",
            color: "#4f46e5",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Article />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1, overflow: "hidden" }}>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.5,
            }}
          >
            <Typography sx={{ fontWeight: 700, minWidth: 0, overflowWrap: "anywhere" }}>
              {formatDocumentLabel(document.documentType, document.side)}
            </Typography>
            <Chip
              size="small"
              color={isApproved ? "success" : isRejected ? "error" : "warning"}
              label={statusLabel}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
            {document.originalName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {document.mimeType} - {formatBytes(document.sizeBytes)}
          </Typography>
        </Box>
      </Box>

      {document.signedUrl ? (
        <Box sx={{ mt: 2 }}>
          {String(document.mimeType || "").startsWith("image/") ? (
            <Box
              component="img"
              src={document.signedUrl}
              alt={document.originalName}
              sx={{
                width: "100%",
                maxWidth: "100%",
                maxHeight: 260,
                objectFit: "contain",
                bgcolor: "#f8fafc",
                borderRadius: 1,
                border: "1px solid #e5e7eb",
              }}
            />
          ) : null}
          <Button
            href={document.signedUrl}
            target="_blank"
            rel="noreferrer"
            startIcon={<OpenInNew />}
            sx={{ mt: 1 }}
          >
            Open signed file
          </Button>
        </Box>
      ) : (
        <Alert severity="warning" sx={{ mt: 2 }}>
          Signed preview URL unavailable.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {!isApproved && (
        <Box sx={{ mt: 2, display: "grid", gap: 1.5 }}>
          <TextField
            size="small"
            label="Rejection reason"
            value={rejectionReason}
            onChange={(event) => setRejectionReason(event.target.value)}
          />
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              color="success"
              startIcon={
                submitting === "approved" ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CheckCircle />
                )
              }
              onClick={() => review("approved")}
              disabled={Boolean(submitting)}
            >
              Approve
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={
                submitting === "rejected" ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Cancel />
                )
              }
              onClick={() => review("rejected")}
              disabled={Boolean(submitting)}
            >
              Reject
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={() => review("reupload_required")}
              disabled={Boolean(submitting)}
            >
              Request re-upload
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default DocumentReviewCard;
